import type { FC, ReactNode } from "react";

import { startCase } from "lodash";

import { Badge, type BadgeProps, classes, Text } from "@alpacahq/ui/old";

type TransferTypeButtonProps = {
  title: string;
  isRecommended?: boolean;
  isHighlighted?: boolean;
  children?: ReactNode;
  isDisabled?: boolean;
  data?: {
    estimated_arrival?: string;
    fees?: string;
    deposit_limit?: string;
    withdraw_limit?: string;
    supported_banks?: string;
  };
  linkedAccount?: {
    imgSrc?: string;
    account: string;
    name: string;
    status?: {
      text: string;
      variant: BadgeProps["variant"];
    };
  };
  onClick: () => void;
  className?: string;
  topBanner?: ReactNode;
};

export const TransferTypeButton: FC<TransferTypeButtonProps> = ({
  title,
  isRecommended,
  isDisabled,
  data,
  onClick,
  className,
  linkedAccount,
  topBanner,
}) => (
  <div
    className={classes(
      "w-full max-w-xl rounded-lg border pt-4 hover:border-black",
      className,
      isDisabled && "cursor-not-allowed opacity-50",
    )}
  >
    {topBanner}
    <div className="flex items-center justify-between gap-4 border-b px-4 pb-4">
      <Text className="text-left font-bold text-gray-700 text-xl">{title}</Text>
      <div className="flex gap-2">
        {isRecommended && <Badge variant="yellow">Recommended</Badge>}
        {linkedAccount && (
          <Badge variant={linkedAccount?.status?.variant ?? "indigo"}>
            {linkedAccount?.status?.text ?? "Linked"}
          </Badge>
        )}
      </div>
    </div>

    {data && (
      <div className="pt-4">
        {Object.entries(data).map(([label, value], index, array) => (
          <div
            key={label}
            className={classes(
              "flex justify-between px-4",
              index !== array.length - 1 && "pb-2",
            )}
          >
            <Text className="whitespace-nowrap text-left text-sm">
              {startCase(label)}
            </Text>
            <Text className="flex text-right font-semibold text-sm">
              {value}
            </Text>
          </div>
        ))}

        <div key="connectedAccount" className="flex justify-between px-4 pt-2">
          <Text className="whitespace-nowrap text-left text-sm">
            Connected Account
          </Text>
          <Text className="flex text-right font-semibold text-sm">
            {linkedAccount
              ? `${linkedAccount.name} (${linkedAccount.account})`
              : "-"}
          </Text>
        </div>
      </div>
    )}

    <button
      type="button"
      className="mt-4 w-full rounded-b-lg bg-gray-100 py-4 hover:bg-black hover:text-white"
      onClick={onClick}
      disabled={isDisabled}
    >
      Select
    </button>
  </div>
);
